<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { NxBanner } from '@blpde/nx-ui';
import { dictionaryFill } from '../../config/dictionaryLayer';
import { DICTIONARY_TEXT } from './labels';

/**
 * How full the written layer's document of a key is (master keeps at most
 * 1 MiB per layer and key): a quiet line, and a warning close to the limit.
 * Hidden while the layer is not known.
 */
const props = defineProps<{
  /** The layer's own document of the key; undefined while the layers are not known. */
  document: unknown;
  /**
   * What shares the document: `spell` (own words, corrections and never-suggest
   * words of both languages in one) or `synonyms` (the synonym groups).
   */
  kind: 'spell' | 'synonyms';
}>();
const { t, locale } = useI18n();
const T = `${DICTIONARY_TEXT}.fill`;

const fill = computed(() => (props.document === undefined ? null : dictionaryFill(props.document)));
const percent = computed(() =>
  fill.value ? new Intl.NumberFormat(locale.value, { maximumFractionDigits: 0 }).format(fill.value.ratio * 100) : '',
);
</script>

<template>
  <NxBanner v-if="fill?.nearLimit" variant="warning" :title="t(`${T}.warningTitle`, { percent })" data-testid="dictionary-fill-warning">
    {{ t(`${T}.warningBody.${kind}`) }}
  </NxBanner>
  <p v-else-if="fill" class="dict-note" data-testid="dictionary-fill">{{ t(`${T}.quiet.${kind}`, { percent }) }}</p>
</template>

<style scoped>
.dict-note {
  margin: 0;
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
}
</style>
